import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Toggle } from 'rsuite';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useProfile } from '@app/api/profile/useProfile';
import { ThemeButton } from '@shared/ui/buttons';
import {
  TelegramInput,
  EmailInput,
  MemberNameInput,
  LanguageSelect,
} from '@shared/ui/forms/inputs';
import Note from '@shared/ui/messages/Note';
import { icons } from '@shared/static/icons';
import {
  useChangePasswordDialog,
  useEnableTwofaDialog,
  useDisableTwofaDialog,
} from '@utils/dialogs-hooks';
import { validateServerErrors } from '@utils/validation';
import { isEmptyObject } from '@utils/helpers';
import { theme } from '@utils/libs/theme';
import WithdrawalChannels from '@widgets/profile/WithdrawalChannels';

export default function ProfileForm() {
  const { openChangePasswordDialog } = useChangePasswordDialog();
  const { user } = useSelector((state) => state.user);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { openEnableTwofaDialog } = useEnableTwofaDialog();
  const { openDisableTwofaDialog } = useDisableTwofaDialog();
  const { updateProfile } = useProfile();
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isDirty, dirtyFields, isSubmitting },
  } = useForm({
    mode: 'onChange',
    values: {
      name: user?.name,
      language: user?.language,
      telegram: user?.telegram,
      email: user?.email,
    },
  });

  async function onSubmit(data) {
    const payload = { name: data.name, language: data.language };
    if (dirtyFields.telegram) {
      payload.telegram = data.telegram;
    }
    try {
      await updateProfile(payload);
    } catch (error) {
      validateServerErrors(setError, error);
    }
  }

  return (
    <div>
      <div className="profile__grid">
        <div>
          <p className="title-h3m mobile">{t('user_info')}</p>
          <div className="form profile-form">
            <div className="formGroup-grid">
              <MemberNameInput control={control} errors={errors} />
              <LanguageSelect control={control} error={errors.language} />
            </div>
            <div className="formGroup-grid">
              <EmailInput
                disabled
                control={control}
                errors={errors}
                changeAction
              />
              <TelegramInput errors={errors} control={control} />
            </div>
            <div className="form-divider" />
            {/* {!user?.group_role && (
              <>
                <WithdrawalChannels />
                <div className="form-divider" />
              </>
            )} */}
            <p className="title-h3m mobile">{t('security')}</p>
            <div className="formGroup-grid">
              <div className="form">
                <button
                  className="btn-toggle"
                  type="button"
                  onClick={
                    user?.two_fa
                      ? openDisableTwofaDialog
                      : openEnableTwofaDialog
                  }
                >
                  <Toggle checked={user?.two_fa} />
                  <p className="secondary-text">
                    {t('two_factor_autentication')}
                  </p>
                </button>
              </div>
              <div>
                <button
                  className="btn-toggle btn-toggle--center"
                  onClick={openChangePasswordDialog}
                  type="button"
                >
                  {icons['refresh']}
                  <p className="fw-500">{t('change_password')}</p>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div>
          <Note
            html
            type="warning"
            title={t('security_disclaimer')}
            content={t('security_disclaimer_content')}
          />
        </div>
      </div>
      <div className={`form-actions ${isMobile ? 'dialog-actions' : ''}`}>
        <ThemeButton
          onClick={handleSubmit(onSubmit)}
          startIcon="check"
          minWidth
          block={isMobile}
          loading={isSubmitting}
          disabled={isSubmitting || !isDirty || !isEmptyObject(errors)}
        >
          {t('save_changes')}
        </ThemeButton>
      </div>
    </div>
  );
}
